import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { QrCode, Copy, Share2, Download, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * QR + Share card for the Business Dashboard.
 * Renders a unique QR code that links to the business' public detail page,
 * plus a copy-link and native share button.
 *
 * The QR PNG is fetched from api.qrserver.com (free, no auth, returns PNG).
 */
export default function BusinessShareCard({ business }) {
  const [copied, setCopied] = useState(false);

  const bizId = business?.id;
  const shareUrl = useMemo(
    () => (bizId ? `${window.location.origin}/businesses/${bizId}?utm=qr` : ""),
    [bizId]
  );

  if (!bizId) return null;

  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=320x320&margin=8&data=${encodeURIComponent(
    shareUrl
  )}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Review link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast.error("Could not copy — please copy manually");
    }
  };

  const nativeShare = async () => {
    const shareData = {
      title: `Review ${business.business_name || "this business"} on Servrevu`,
      text: `Leave a review for ${business.business_name || "us"} on Servrevu:`,
      url: shareUrl,
    };
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch {
        // user cancelled or unsupported — fall back to copy
        copyLink();
      }
    } else {
      copyLink();
    }
  };

  const downloadQR = () => {
    const a = document.createElement("a");
    a.href = qrSrc;
    a.download = `${(business.business_name || "business").replace(/\W+/g, "-").toLowerCase()}-servrevu-qr.png`;
    a.target = "_blank";
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <Card className="rounded-md border border-border p-6" data-testid="business-share-card">
      <div className="flex items-center gap-2 mb-2">
        <QrCode size={18} className="text-[#B31E1E]" />
        <h3 className="font-display text-xl">Share & collect reviews</h3>
      </div>
      <p className="text-sm text-gray-600 leading-relaxed max-w-2xl">
        This unique QR code links straight to your public review page. Print it at the register,
        add it to invoices, or share the link in email signatures and receipts.
      </p>

      <div className="mt-6 flex flex-col md:flex-row items-start gap-6">
        <div className="shrink-0 border border-border rounded-md bg-white p-3 shadow-sm">
          <img
            src={qrSrc}
            alt={`QR code for ${business.business_name || "business"}`}
            className="h-44 w-44 block"
            data-testid="business-qr-img"
          />
        </div>
        <div className="flex-1 w-full space-y-3">
          <div>
            <p className="overline text-gray-500">Public review link</p>
            <p
              className="mt-1 text-sm font-mono break-all bg-gray-50 border border-border rounded-md px-3 py-2"
              data-testid="business-share-url"
            >
              {shareUrl}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={copyLink}
              variant="outline"
              size="sm"
              className="rounded-[35px] shadow-sm hover:shadow-md"
              data-testid="business-share-copy"
            >
              {copied ? <Check size={14} className="mr-1" /> : <Copy size={14} className="mr-1" />}
              {copied ? "Copied" : "Copy link"}
            </Button>
            <Button
              onClick={nativeShare}
              size="sm"
              className="rounded-[35px] shadow-sm hover:shadow-md bg-[#B31E1E] hover:bg-[#8F1717]"
              data-testid="business-share-native"
            >
              <Share2 size={14} className="mr-1" /> Share
            </Button>
            <Button
              onClick={downloadQR}
              variant="outline"
              size="sm"
              className="rounded-[35px] shadow-sm hover:shadow-md"
              data-testid="business-qr-download"
            >
              <Download size={14} className="mr-1" /> Download QR
            </Button>
          </div>
          <p className="text-[11px] text-gray-500">
            Tip: a printed QR at the point of sale typically lifts review volume 3–5×.
          </p>
        </div>
      </div>
    </Card>
  );
}
